import type { ElementRef } from '@angular/core';
import { Component, computed, input, signal, viewChild } from '@angular/core';

import { Icon } from '../icon/icon';
import { VideoPlayerComponent } from '../video-player/video-player';

@Component({
  selector: 'app-comparison-slider',
  imports: [Icon, VideoPlayerComponent],
  templateUrl: './comparison-slider.html',
  styleUrl: './comparison-slider.scss',
})
export class ComparisonSlider {
  readonly beforeSrc = input.required<string>();
  readonly afterSrc = input.required<string>();
  readonly beforeLabel = input('Before');
  readonly afterLabel = input('After');

  private readonly frame = viewChild.required<ElementRef<HTMLElement>>('frame');

  /** Divider position as a percentage from the left edge (0–100). */
  protected readonly position = signal(50);
  private dragging = false;

  /** Clip the "before" layer so only the portion left of the divider is visible. */
  protected readonly beforeClip = computed(() => `inset(0 ${100 - this.position()}% 0 0)`);

  protected onPointerDown(event: PointerEvent): void {
    this.dragging = true;
    this.frame().nativeElement.setPointerCapture(event.pointerId);
    this.updateFromEvent(event);
  }

  protected onPointerMove(event: PointerEvent): void {
    if (!this.dragging) return;
    this.updateFromEvent(event);
  }

  protected onPointerUp(): void {
    this.dragging = false;
  }

  protected onKeydown(event: KeyboardEvent): void {
    const step = event.shiftKey ? 10 : 2;

    switch (event.key) {
      case 'ArrowLeft':
        this.position.update((p) => Math.max(0, p - step));
        break;
      case 'ArrowRight':
        this.position.update((p) => Math.min(100, p + step));
        break;
      case 'Home':
        this.position.set(0);
        break;
      case 'End':
        this.position.set(100);
        break;
      default:
        return;
    }

    event.preventDefault();
  }

  private updateFromEvent(event: PointerEvent): void {
    const rect = this.frame().nativeElement.getBoundingClientRect();
    const pct = ((event.clientX - rect.left) / rect.width) * 100;
    this.position.set(Math.min(100, Math.max(0, Math.round(pct))));
  }
}
